import { useState, useCallback, useMemo, useRef } from 'react';

/**
 * Hook to DRY up common handlers for tracking focus & blur, and get tidier
 * props to spread onto components for common use-cases.
 */
export function useTrackFocus(options?: { onFocus?: React.FocusEventHandler; onBlur?: React.FocusEventHandler }) {
  const [isFocused, setFocused] = useState(false);
  const state = useRef({
    isFocusWithin: false,
  });
  const onFocus = options?.onFocus;
  const onBlur = options?.onBlur;

  const trackFocus = useCallback(
    (e: React.FocusEvent) => {
      setFocused(true);
      state.current.isFocusWithin = true;
      onFocus && onFocus(e);
    },
    [onFocus]
  );

  const trackBlur = useCallback(
    (e: React.FocusEvent) => {
      setFocused(false);
      onBlur && onBlur(e);
      state.current.isFocusWithin = true;
    },
    [onBlur]
  );

  const focusTrackingProps = useMemo(
    () => ({
      onFocus: trackFocus,
      onBlur: trackBlur,
    }),
    [trackBlur, trackFocus]
  );

  return {
    /**
     *  mutable ref to state, to do not trigger useEffect hooks
     */
    state,
    /**
     * Use to respond to focus
     */
    isFocused,
    /**
     * Use if you have other focus handling concerns that don't fit in the `onFocus` arg
     */
    trackFocus,
    /**
     * Use if you have other blur handling concerns that don't fit in the `onBlur` arg
     */
    trackBlur,
    /**
     * Spread onto target element, i.e. `{...focusTrackingProps}`, if you don't
     * have other focus/blur handling concerns.
     */
    focusTrackingProps,
  };
}
